import * as z from 'zod';

export const GetFormRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which the form belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetFormResponse = z.object({
  controls: z
    .array(
      z.object({
        type: z
          .enum([
            'INPUT',
            'TEXTAREA',
            'DROPDOWN',
            'RADIO',
            'CHECKBOX',
            'NAME',
            'GUEST_CONTROL',
            'ADDRESS_SHORT',
            'ADDRESS_FULL',
            'DATE',
          ])
          .describe('Field control type.')
          .optional(),
        system: z
          .boolean()
          .describe(
            'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
          )
          .optional(),
        name: z.string().describe('Deprecated: Use `id` or `_id`.').optional(),
        inputs: z
          .array(
            z.object({
              name: z.string().describe('Field name.').optional(),
              array: z
                .boolean()
                .describe(
                  '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                )
                .optional(),
              label: z.string().describe('Main field label.').optional(),
              additionalLabels: z
                .record(z.string(), z.string())
                .describe(
                  'Additional labels for multi-valued fields such as address.'
                )
                .optional(),
              options: z.array(z.string()).optional(),
              mandatory: z
                .boolean()
                .describe('Whether field is mandatory.')
                .optional(),
              maxLength: z
                .number()
                .int()
                .describe(
                  'Maximum number of accepted characters (relevant for text fields).'
                )
                .optional(),
              type: z
                .enum(['TEXT', 'NUMBER', 'TEXT_ARRAY', 'DATE_TIME', 'ADDRESS'])
                .describe(
                  'Type which determines field format.\nUsed to validate submitted response.'
                )
                .optional(),
              maxSize: z
                .number()
                .int()
                .describe(
                  'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                )
                .optional()
                .nullable(),
              defaultOptionSelection: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      optionIndex: z.never().optional(),
                      placeholderText: z.never().optional(),
                    }),
                    z.object({
                      placeholderText: z.never().optional(),
                      optionIndex: z
                        .number()
                        .int()
                        .describe(
                          '0-based index from predefined `controls.inputs.options` which is initial selection.'
                        )
                        .min(0)
                        .max(199),
                    }),
                    z.object({
                      optionIndex: z.never().optional(),
                      placeholderText: z
                        .string()
                        .describe(
                          'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                        )
                        .max(200),
                    }),
                  ])
                )
                .describe(
                  'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                )
                .optional(),
              labels: z
                .array(
                  z.object({
                    name: z.string().describe('Field name.').optional(),
                    label: z.string().describe('Field label.').optional(),
                  })
                )
                .optional(),
            })
          )
          .optional(),
        label: z
          .string()
          .describe('*Deprecated:** Use `controls.inputs.label`.')
          .optional(),
        orderIndex: z
          .number()
          .int()
          .describe(
            'Field controls are sorted by this value in ascending order.'
          )
          .optional(),
        _id: z.string().describe('Unique control ID.').optional(),
        deleted: z
          .boolean()
          .describe('Whether the input control is deleted.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  messages: z
    .object({
      rsvp: z
        .object({
          rsvpYesOption: z
            .string()
            .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
            .optional(),
          rsvpNoOption: z
            .string()
            .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
            .optional(),
          positiveMessages: z
            .object({
              title: z
                .string()
                .describe('Main form title for positive response.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  addToCalendarActionLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareActionLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after registration.')
                .optional(),
            })
            .describe(
              'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
            )
            .optional(),
          waitlistMessages: z
            .object({
              title: z
                .string()
                .describe('Main form title for positive response.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  addToCalendarActionLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareActionLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after registration.')
                .optional(),
            })
            .describe(
              'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
            )
            .optional(),
          negativeMessages: z
            .object({
              title: z
                .string()
                .describe('Main form title for negative response.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  shareActionLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after registration.')
                .optional(),
            })
            .describe(
              'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
            )
            .optional(),
          submitActionLabel: z
            .string()
            .describe('"Submit form" call-to-action label text.')
            .optional(),
        })
        .describe(
          '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
        )
        .optional(),
      checkout: z
        .object({
          title: z
            .string()
            .describe('Main form title for response.')
            .optional(),
          submitActionLabel: z
            .string()
            .describe('Submit form call-to-action label text.')
            .optional(),
          confirmation: z
            .object({
              title: z
                .string()
                .describe('Confirmation message title.')
                .optional(),
              message: z
                .string()
                .describe('Confirmation message text.')
                .optional(),
              downloadTicketsLabel: z
                .string()
                .describe('"Download tickets" call-to-action label text.')
                .optional(),
              addToCalendarLabel: z
                .string()
                .describe('"Add to calendar" call-to-action label text.')
                .optional(),
              shareEventLabel: z
                .string()
                .describe('"Share event" call-to-action label text.')
                .optional(),
            })
            .describe('Confirmation messages shown after checkout.')
            .optional(),
        })
        .describe('Checkout form messages.')
        .optional(),
      registrationClosed: z
        .object({
          message: z
            .string()
            .describe('Message shown when event registration is closed.')
            .optional(),
          exploreEventsActionLabel: z
            .string()
            .describe('"Explore other events" call-to-action label text.')
            .optional(),
        })
        .describe('Messages shown when event registration is closed.')
        .optional(),
      ticketsUnavailable: z
        .object({
          message: z
            .string()
            .describe('Message shown when event tickets are unavailable.')
            .optional(),
          exploreEventsActionLabel: z
            .string()
            .describe('"Explore other events" call-to-action label text.')
            .optional(),
        })
        .describe('Messages shown when event tickets are unavailable.')
        .optional(),
    })
    .describe(
      'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
    )
    .optional(),
});
export const AddControlRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which the form belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          radioButton: z.never().optional(),
          phone: z
            .object({
              label: z
                .string()
                .describe('Phone input label.')
                .max(100)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether the phone input is required.')
                .optional(),
            })
            .describe('Phone number input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          radioButton: z.never().optional(),
          address: z
            .object({
              labels: z
                .object({
                  addressLine: z
                    .string()
                    .describe('Single-line address input label.')
                    .max(100)
                    .optional(),
                  country: z
                    .string()
                    .describe('Country input label.')
                    .max(100)
                    .optional(),
                  subdivision: z
                    .string()
                    .describe('Subdivision input label.')
                    .max(100)
                    .optional(),
                  city: z
                    .string()
                    .describe('City input label.')
                    .max(100)
                    .optional(),
                  postalCode: z
                    .string()
                    .describe('Postal code input label.')
                    .max(100)
                    .optional(),
                  streetAddress: z
                    .string()
                    .describe('Street address input label.')
                    .max(100)
                    .optional(),
                })
                .describe('Address control labels for each input.')
                .optional(),
              full: z
                .boolean()
                .describe(
                  'Whether an address is multi-line (consisting of multiple fields such as country, city, postal code). When `false`, address is single-line.'
                )
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether the address input is required.')
                .optional(),
            })
            .describe('Single-line or full address input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          radioButton: z.never().optional(),
          date: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(100)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether the date input is required.')
                .optional(),
            })
            .describe('Day, month, year date input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          radioButton: z.never().optional(),
          additionalGuests: z
            .object({
              labels: z
                .object({
                  single: z
                    .string()
                    .describe('Input label for a single guest.')
                    .max(200)
                    .optional(),
                  multiple: z
                    .string()
                    .describe('Input label for multiple guests.')
                    .max(200)
                    .optional(),
                })
                .describe('Additional guests control labels for each input.')
                .optional(),
              namesMandatory: z
                .boolean()
                .describe('Whether the individual guest names are required.')
                .optional(),
              maxGuests: z
                .number()
                .int()
                .describe('Maximum number of additional guests.')
                .min(1)
                .max(10)
                .optional(),
            })
            .describe('Additional guests input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          radioButton: z.never().optional(),
          dropdown: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              options: z.array(z.string()).min(2).max(200).optional(),
              mandatory: z
                .boolean()
                .describe('Whether a choice is required.')
                .optional(),
              defaultOptionSelection: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      optionIndex: z.never().optional(),
                      placeholderText: z.never().optional(),
                    }),
                    z.object({
                      placeholderText: z.never().optional(),
                      optionIndex: z
                        .number()
                        .int()
                        .describe(
                          '0-based index from predefined `controls.inputs.options` which is initial selection.'
                        )
                        .min(0)
                        .max(199),
                    }),
                    z.object({
                      optionIndex: z.never().optional(),
                      placeholderText: z
                        .string()
                        .describe(
                          'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                        )
                        .max(200),
                    }),
                  ])
                )
                .describe(
                  'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.'
                )
                .optional(),
            })
            .describe('Single-choice dropdown style input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          text: z.never().optional(),
          radioButton: z.never().optional(),
          checkbox: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether at least one checkbox is required.')
                .optional(),
              options: z.array(z.string()).min(1).max(200).optional(),
            })
            .describe('Multiple-choice checkbox style input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          radioButton: z.never().optional(),
          text: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether a text input is required.')
                .optional(),
              maxLength: z
                .number()
                .int()
                .describe('Maximum number of characters allowed.')
                .min(0)
                .max(1000)
                .optional(),
              multiLine: z
                .boolean()
                .describe(
                  'Whether the input control should allow multiple lines in text.'
                )
                .optional(),
              comment: z
                .boolean()
                .describe(
                  'Whether the input control should be displayed as a comment.'
                )
                .optional(),
            })
            .describe('Free-form text input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          radioButton: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              options: z.array(z.string()).min(2).max(200).optional(),
            })
            .describe('Single-choice radio button style input control.'),
        }),
      ])
    )
    .describe('Optional fields.'),
});
export const AddControlResponse = z.object({
  _id: z.string().describe('Generated unique input control ID.').optional(),
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe('Modified event form.')
    .optional(),
});
export const UpdateControlRequest = z.object({
  identifiers: z
    .object({
      eventId: z
        .string()
        .describe('Event ID to which the form belongs.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      _id: z.string().describe('Unique input control ID.'),
    })
    .describe('Identifies what form to update.'),
  options: z
    .intersection(
      z.object({
        orderIndex: z
          .number()
          .int()
          .describe('Index used to sort input controls in ascending order.')
          .optional(),
      }),
      z.xor([
        z.object({
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          phone: z
            .object({
              label: z
                .string()
                .describe('Phone input label.')
                .max(100)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether the phone input is required.')
                .optional(),
            })
            .describe('Phone number input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          address: z
            .object({
              labels: z
                .object({
                  addressLine: z
                    .string()
                    .describe('Single-line address input label.')
                    .max(100)
                    .optional(),
                  country: z
                    .string()
                    .describe('Country input label.')
                    .max(100)
                    .optional(),
                  subdivision: z
                    .string()
                    .describe('Subdivision input label.')
                    .max(100)
                    .optional(),
                  city: z
                    .string()
                    .describe('City input label.')
                    .max(100)
                    .optional(),
                  postalCode: z
                    .string()
                    .describe('Postal code input label.')
                    .max(100)
                    .optional(),
                  streetAddress: z
                    .string()
                    .describe('Street address input label.')
                    .max(100)
                    .optional(),
                })
                .describe('Address control labels for each input.')
                .optional(),
              full: z
                .boolean()
                .describe(
                  'Whether an address is multi-line (consisting of multiple fields such as country, city, postal code). When `false`, address is single-line.'
                )
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether the address input is required.')
                .optional(),
            })
            .describe('Single-line or full address input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          date: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(100)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether the date input is required.')
                .optional(),
            })
            .describe('Day, month, year date input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          additionalGuests: z
            .object({
              labels: z
                .object({
                  single: z
                    .string()
                    .describe('Input label for a single guest.')
                    .max(200)
                    .optional(),
                  multiple: z
                    .string()
                    .describe('Input label for multiple guests.')
                    .max(200)
                    .optional(),
                })
                .describe('Additional guests control labels for each input.')
                .optional(),
              namesMandatory: z
                .boolean()
                .describe('Whether the individual guest names are required.')
                .optional(),
              maxGuests: z
                .number()
                .int()
                .describe('Maximum number of additional guests.')
                .min(1)
                .max(10)
                .optional(),
            })
            .describe('Additional guests input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          dropdown: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              options: z.array(z.string()).min(2).max(200).optional(),
              mandatory: z
                .boolean()
                .describe('Whether a choice is required.')
                .optional(),
              defaultOptionSelection: z
                .intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      optionIndex: z.never().optional(),
                      placeholderText: z.never().optional(),
                    }),
                    z.object({
                      placeholderText: z.never().optional(),
                      optionIndex: z
                        .number()
                        .int()
                        .describe(
                          '0-based index from predefined `controls.inputs.options` which is initial selection.'
                        )
                        .min(0)
                        .max(199),
                    }),
                    z.object({
                      optionIndex: z.never().optional(),
                      placeholderText: z
                        .string()
                        .describe(
                          'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                        )
                        .max(200),
                    }),
                  ])
                )
                .describe(
                  'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.'
                )
                .optional(),
            })
            .describe('Single-choice dropdown style input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          checkbox: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether at least one checkbox is required.')
                .optional(),
              options: z.array(z.string()).min(1).max(200).optional(),
            })
            .describe('Multiple-choice checkbox style input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          text: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              mandatory: z
                .boolean()
                .describe('Whether a text input is required.')
                .optional(),
              maxLength: z
                .number()
                .int()
                .describe('Maximum number of characters allowed.')
                .min(0)
                .max(1000)
                .optional(),
              multiLine: z
                .boolean()
                .describe(
                  'Whether the input control should allow multiple lines in text.'
                )
                .optional(),
              comment: z
                .boolean()
                .describe(
                  'Whether the input control should be displayed as a comment.'
                )
                .optional(),
            })
            .describe('Free-form text input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          email: z.never().optional(),
          radioButton: z.never().optional(),
          name: z
            .object({
              labels: z
                .object({
                  firstName: z
                    .string()
                    .describe('First name input label.')
                    .max(100)
                    .optional(),
                  lastName: z
                    .string()
                    .describe('Last name input label .')
                    .max(100)
                    .optional(),
                })
                .describe('Name control labels of each input.')
                .optional(),
            })
            .describe('Main guest name input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          radioButton: z.never().optional(),
          email: z
            .object({
              label: z
                .string()
                .describe('Email input label.')
                .max(100)
                .optional(),
            })
            .describe('Main guest email input control.'),
        }),
        z.object({
          phone: z.never().optional(),
          address: z.never().optional(),
          date: z.never().optional(),
          additionalGuests: z.never().optional(),
          dropdown: z.never().optional(),
          checkbox: z.never().optional(),
          text: z.never().optional(),
          name: z.never().optional(),
          email: z.never().optional(),
          radioButton: z
            .object({
              label: z
                .string()
                .describe('Input control label.')
                .max(200)
                .optional(),
              options: z.array(z.string()).min(2).max(200).optional(),
            })
            .describe('Single-choice radio style input control.'),
        }),
      ])
    )
    .describe('Optional fields.'),
});
export const UpdateControlResponse = z.object({
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe('Modified event form.')
    .optional(),
});
export const DeleteControlRequest = z.object({
  identifiers: z
    .object({
      eventId: z
        .string()
        .describe('Event ID to which the form belongs.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      _id: z.string().describe('Unique input control ID.').max(100),
    })
    .describe('Identifies what form to delete.'),
});
export const DeleteControlResponse = z.object({
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe('Modified event form')
    .optional(),
});
export const UpdateMessagesRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which the form belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .object({ paths: z.array(z.string()) })
        .describe(
          'Set of field paths, specifying which parts of this resource to update.\nWhen fields are empty, request is interpreted as full update.\nBehavior follows [google.protobuf.FieldMask](https://developers.google.com/protocol-buffers/docs/reference/google.protobuf#google.protobuf.FieldMask) semantics.'
        )
        .optional()
        .nullable(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe('Set of configured form messages to update.')
        .optional(),
    })
    .describe('Optional fields.')
    .optional(),
});
export const UpdateMessagesResponse = z.object({
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe('Modified event form.')
    .optional(),
});
export const PublishDraftRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which the form belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const PublishDraftResponse = z.object({
  form: z
    .object({
      controls: z
        .array(
          z.object({
            type: z
              .enum([
                'INPUT',
                'TEXTAREA',
                'DROPDOWN',
                'RADIO',
                'CHECKBOX',
                'NAME',
                'GUEST_CONTROL',
                'ADDRESS_SHORT',
                'ADDRESS_FULL',
                'DATE',
              ])
              .describe('Field control type.')
              .optional(),
            system: z
              .boolean()
              .describe(
                'Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed.'
              )
              .optional(),
            name: z
              .string()
              .describe('Deprecated: Use `id` or `_id`.')
              .optional(),
            inputs: z
              .array(
                z.object({
                  name: z.string().describe('Field name.').optional(),
                  array: z
                    .boolean()
                    .describe(
                      '*Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.'
                    )
                    .optional(),
                  label: z.string().describe('Main field label.').optional(),
                  additionalLabels: z
                    .record(z.string(), z.string())
                    .describe(
                      'Additional labels for multi-valued fields such as address.'
                    )
                    .optional(),
                  options: z.array(z.string()).optional(),
                  mandatory: z
                    .boolean()
                    .describe('Whether field is mandatory.')
                    .optional(),
                  maxLength: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of accepted characters (relevant for text fields).'
                    )
                    .optional(),
                  type: z
                    .enum([
                      'TEXT',
                      'NUMBER',
                      'TEXT_ARRAY',
                      'DATE_TIME',
                      'ADDRESS',
                    ])
                    .describe(
                      'Type which determines field format.\nUsed to validate submitted response.'
                    )
                    .optional(),
                  maxSize: z
                    .number()
                    .int()
                    .describe(
                      'The maximum number of accepted values for array input.\n\n**Note:** Only applicable for `TEXT_ARRAY` input fields.'
                    )
                    .optional()
                    .nullable(),
                  defaultOptionSelection: z
                    .intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z.never().optional(),
                        }),
                        z.object({
                          placeholderText: z.never().optional(),
                          optionIndex: z
                            .number()
                            .int()
                            .describe(
                              '0-based index from predefined `controls.inputs.options` which is initial selection.'
                            )
                            .min(0)
                            .max(199),
                        }),
                        z.object({
                          optionIndex: z.never().optional(),
                          placeholderText: z
                            .string()
                            .describe(
                              'Placeholder hint describing expected choices, such as "Please select".\nConsidered an empty choice.'
                            )
                            .max(200),
                        }),
                      ])
                    )
                    .describe(
                      'Default option initially selected when an input has multiple choices.\n\nDefaults to first (0th) option, if not configured.\nCurrently only applicable for `type.dropdown`.'
                    )
                    .optional(),
                  labels: z
                    .array(
                      z.object({
                        name: z.string().describe('Field name.').optional(),
                        label: z.string().describe('Field label.').optional(),
                      })
                    )
                    .optional(),
                })
              )
              .optional(),
            label: z
              .string()
              .describe('*Deprecated:** Use `controls.inputs.label`.')
              .optional(),
            orderIndex: z
              .number()
              .int()
              .describe(
                'Field controls are sorted by this value in ascending order.'
              )
              .optional(),
            _id: z.string().describe('Unique control ID.').optional(),
            deleted: z
              .boolean()
              .describe('Whether the input control is deleted.')
              .optional()
              .nullable(),
          })
        )
        .optional(),
      messages: z
        .object({
          rsvp: z
            .object({
              rsvpYesOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"YES"`.')
                .optional(),
              rsvpNoOption: z
                .string()
                .describe('Label text indicating RSVP\'s `status` is `"NO"`.')
                .optional(),
              positiveMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"YES"`.'
                )
                .optional(),
              waitlistMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for positive response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      message: z
                        .string()
                        .describe('Confirmation message text.')
                        .optional(),
                      addToCalendarActionLabel: z
                        .string()
                        .describe(
                          '"Add to calendar" call-to-action label text.'
                        )
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available).'
                )
                .optional(),
              negativeMessages: z
                .object({
                  title: z
                    .string()
                    .describe('Main form title for negative response.')
                    .optional(),
                  confirmation: z
                    .object({
                      title: z
                        .string()
                        .describe('Confirmation message title.')
                        .optional(),
                      shareActionLabel: z
                        .string()
                        .describe('"Share event" call-to-action label text.')
                        .optional(),
                    })
                    .describe('Confirmation messages shown after registration.')
                    .optional(),
                })
                .describe(
                  'Messages displayed when an RSVP\'s `status` is set to `"NO"`.'
                )
                .optional(),
              submitActionLabel: z
                .string()
                .describe('"Submit form" call-to-action label text.')
                .optional(),
            })
            .describe(
              '[RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages.'
            )
            .optional(),
          checkout: z
            .object({
              title: z
                .string()
                .describe('Main form title for response.')
                .optional(),
              submitActionLabel: z
                .string()
                .describe('Submit form call-to-action label text.')
                .optional(),
              confirmation: z
                .object({
                  title: z
                    .string()
                    .describe('Confirmation message title.')
                    .optional(),
                  message: z
                    .string()
                    .describe('Confirmation message text.')
                    .optional(),
                  downloadTicketsLabel: z
                    .string()
                    .describe('"Download tickets" call-to-action label text.')
                    .optional(),
                  addToCalendarLabel: z
                    .string()
                    .describe('"Add to calendar" call-to-action label text.')
                    .optional(),
                  shareEventLabel: z
                    .string()
                    .describe('"Share event" call-to-action label text.')
                    .optional(),
                })
                .describe('Confirmation messages shown after checkout.')
                .optional(),
            })
            .describe('Checkout form messages.')
            .optional(),
          registrationClosed: z
            .object({
              message: z
                .string()
                .describe('Message shown when event registration is closed.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event registration is closed.')
            .optional(),
          ticketsUnavailable: z
            .object({
              message: z
                .string()
                .describe('Message shown when event tickets are unavailable.')
                .optional(),
              exploreEventsActionLabel: z
                .string()
                .describe('"Explore other events" call-to-action label text.')
                .optional(),
            })
            .describe('Messages shown when event tickets are unavailable.')
            .optional(),
        })
        .describe(
          'Set of defined form messages displayed in the UI before, during, and after a registration flow.\nIncludes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.'
        )
        .optional(),
    })
    .describe('Event form.')
    .optional(),
});
export const DiscardDraftRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which the form belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DiscardDraftResponse = z.object({});
